<template>
  <PanelPage ref="useAlerts" :loading-overlay="loadingOverlay" md="8">
    <v-card title="Explore PaymentOrders">
      <v-data-table-server
        v-model:items-per-page="itemsPerPage"
        :headers="headers"
        :items="serverItems"
        :items-length="totalItems"
        :loading="loading"
        :search="search"
        @update:options="loadItems"
      >
        <template v-slot:top>
          <v-container class="pt-2">
            <v-row>
              <v-col>
                <v-autocomplete
                  label="Filter per Park name"
                  density="compact"
                  :items="parks"
                  item-title="name"
                  item-value="id"
                  v-model="parkid"
                  hide-details
                />
              </v-col>
              <v-col>
                <v-dialog width="auto">
                  <template v-slot:activator="{ props: activatorProps }">
                    <v-text-field
                      v-bind="activatorProps"
                      label="Min Confirm Time"
                      density="compact"
                      readonly
                      v-model="isoStartDate"
                      hide-details
                      clearable
                      persistent-clear
                      clear-icon="mdi-backspace-outline"
                      @click:clear="filterStartDate = null"
                    />
                  </template>
                  <template v-slot:default="{ isActive }">
                    <v-date-picker
                      v-model="filterStartDate"
                      @update:model-value="isActive.value = false"
                    />
                  </template>
                </v-dialog>
              </v-col>
              <v-col>
                <v-checkbox
                  label="Only completed charges"
                  density="compact"
                  v-model="onlyCompleted"
                  hide-details
                />
              </v-col>
            </v-row>
          </v-container>
        </template>
        <!--suppress VueUnrecognizedSlot -->
        <template v-slot:item.actions="{ item }">
          <v-dialog
            max-width="500px"
          >
            <template v-slot:activator="{ props: activatorProps }">
              <v-btn
                v-bind="activatorProps"
                prepend-icon="mdi-email-arrow-right-outline"
                text="Receipt"
              />
            </template>
            <template v-slot:default="{ isActive }">
              <v-form @submit.prevent="async (e) => {bnumForSend = item['id']; await submit(e); isActive.value = false}"
                      @reset="handleReset(); isActive.value = false">
                <v-card title="Send Receipt">
                  <v-card-text>
                    <v-text-field
                      density="comfortable"
                      v-model="form.email.value.value"
                      :error-messages="form.email.errorMessage.value"
                      label="Client Email"
                    />
                    <v-text-field
                      density="comfortable"
                      v-model="form.cc.value.value"
                      :error-messages="form.cc.errorMessage.value"
                      label="CC Email (it can be RossiniEnergy)"
                    />
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                      type="reset"
                      prepend-icon="mdi-close"
                      text="Close Dialog"
                      color="red"
                    ></v-btn>
                    <v-btn
                      type="submit"
                      prepend-icon="mdi-send"
                      :loading="sendLoading"
                      text="Send"
                      color="green"
                    ></v-btn>
                  </v-card-actions>
                </v-card>
              </v-form>
            </template>
          </v-dialog>

        </template>
      </v-data-table-server>
    </v-card>
  </PanelPage>
</template>

<script setup>
import { onMounted, ref, watch, computed } from "vue";
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { getFormFromSchema } from "../utils.js";
import { fetchPaymentOrders, sendReceiptPaymentOrder } from "../remote/paymentorder.js";
import PanelPage from "../components/PanelPage.vue";
import axios from 'axios';
import { endpoint } from "../remote/utils.js";

// State
const useAlerts = ref(null);
const loadingOverlay = ref(false);
const parkid = ref(null);
const parks = ref([]);
const search = ref('');
const onlyCompleted = ref(true);
const sendLoading = ref(false);
const bnumForSend = ref(0);
const filterStartDate = ref(null);

const isoStartDate = computed(() => {
  if (filterStartDate.value) {
    return filterStartDate.value.toLocaleString();
  } else {
    return null;
  }
});

const itemsPerPage = ref(10);
const serverItems = ref([]);
const loading = ref(true);
const totalItems = ref(0);

// Fixed
const headers = ref([
  { title: 'ID', key: 'id', align: 'start' },
  { title: 'Bnum', key: 'chargingstation', align: 'center' },
  { title: 'Phase', key: 'phase', align: 'center' },
  // TODO: add charge start time
  { title: 'Confirm Date', key: 'confirm_timestamp', align: 'center' },
  { title: 'Total Price (€)', key: 'total_cents', align: 'center' },
  { title: 'Actions', key: 'actions', align: 'right', sortable: false },
]);
const allowedKeys = ref(headers.value.map(obj => obj.key));

async function loadItems({ page, itemsPerPage, sortBy }) {
  loading.value = true;
  let getParams = {};
  if (parkid.value !== null) {
    getParams.park = parkid.value;
  }
  if (onlyCompleted.value) {
    getParams.phase = 3;
  }
  if (filterStartDate.value) {
    // noinspection JSUnresolvedReference
    getParams.confirm_timestamp__gte = filterStartDate.value.toISOString();
  }
  const res = await fetchPaymentOrders({ page, itemsPerPage, sortBy }, getParams);
  console.log(res);
  res['results'].forEach(obj => {
    if (obj['confirm_timestamp'] !== null) {
      const date = new Date(obj['confirm_timestamp']);
      obj['confirm_timestamp'] = date.toLocaleString();
    }
    obj['total_cents'] = (obj['total_cents'] / 100).toFixed(2);
    Object.keys(obj).forEach(key => {
      // noinspection JSUnresolvedReference
      if (!allowedKeys.value.includes(key)) {
        delete obj[key];
      }
    });
  });
  serverItems.value = res['results'];
  totalItems.value = res['count'];
  loading.value = false;
}

watch(parkid, () => {
  search.value = String(Date.now());
});

watch(onlyCompleted, () => {
  search.value = String(Date.now());
});

watch(filterStartDate, () => {
  search.value = String(Date.now());
});

onMounted(async () => {
  const res = await axios.get(endpoint('internal/current/access_status/'), {
    withCredentials: true
  });
  parks.value = res.data;
});

// Form for send receipt
// noinspection JSCheckFunctionSignatures
const schema = yup.object({
  email: yup.string().email().required(),
  cc: yup.string().email(),
});

const { handleSubmit, handleReset } = useForm({
  validationSchema: schema
});

const form = getFormFromSchema(schema);

const submit = handleSubmit(async (values) => {
  sendLoading.value = true;
  let ccs = [];
  if (values['cc']) {
    ccs.push(values['cc']);
  }
  try {
    const sent = await sendReceiptPaymentOrder(bnumForSend.value, values['email'], ccs);
    if (sent) {
      useAlerts.value.successAlert(`Operation completed`, `Receipt sent!`);
    } else {
      useAlerts.value.errorAlert(`Unknown Error`, `No internal error, but the mail has not been sent!`);
    }
  } catch (e) {
    if (e.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      useAlerts.value.errorAlert(`Error ${e.response.status}`, `${JSON.stringify(e.response.data)}`);
    } else if (e.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser
      useAlerts.value.errorAlert(`No response received!`, `${JSON.stringify(e.request)}`);
    } else {
      // Something happened in setting up the request that triggered an Error
      useAlerts.value.errorAlert(`Unexpected Error!`, `${JSON.stringify(e.message)}`);
    }
    throw e;
  } finally {
    sendLoading.value = false;
  }
});
</script>

<style scoped>

</style>
